import { openDB, DBSchema, IDBPDatabase } from "idb";

interface ParsedEvent {
  uid: string;
  summary: string;
  description?: string;
  location?: string;
  speakers?: string;
  audience?: string;
  start: string; // ISO string for storage
  end?: string; // ISO string for storage
  allDay?: boolean;
}

interface EventsDBSchema extends DBSchema {
  events: {
    key: string; // masjidId
    value: {
      masjidId: number;
      events: ParsedEvent[];
      lastFetch: number; // Timestamp of last fetch
      eTag?: string; // Optional ETag for change detection
    };
  };
}

class EventsDatabase {
  private dbPromise: Promise<IDBPDatabase<EventsDBSchema>> | null = null;

  private initDB(): Promise<IDBPDatabase<EventsDBSchema>> {
    return openDB<EventsDBSchema>("M247EventsDB", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("events")) {
          db.createObjectStore("events");
        }
      },
    });
  }

  private async getDB(): Promise<IDBPDatabase<EventsDBSchema>> {
    if (!this.dbPromise) {
      this.dbPromise = this.initDB();
    }
    try {
      return await this.dbPromise;
    } catch (error) {
      console.warn("IndexedDB open failed, attempting recovery:", error);
      this.dbPromise = null;
      try {
        await new Promise<void>((resolve, reject) => {
          const req = indexedDB.deleteDatabase("M247EventsDB");
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
          req.onblocked = () => resolve();
        });
        console.warn("Corrupted M247EventsDB deleted, recreating");
      } catch (deleteErr) {
        console.error("Failed to delete corrupted DB:", deleteErr);
      }
      this.dbPromise = this.initDB();
      return this.dbPromise;
    }
  }

  private getKey(masjidId: number): string {
    return `events_${masjidId}`;
  }

  async getEvents(masjidId: number): Promise<{
    events: ParsedEvent[];
    lastFetch: number;
    eTag?: string;
  } | null> {
    try {
      const db = await this.getDB();
      const key = this.getKey(masjidId);
      const result = await db.get("events", key);
      if (result) {
        return {
          events: result.events,
          lastFetch: result.lastFetch,
          eTag: result.eTag,
        };
      }
      return null;
    } catch (error) {
      console.error("Error getting events from IndexedDB:", error);
      return null;
    }
  }

  async saveEvents(
    masjidId: number,
    events: ParsedEvent[],
    eTag?: string,
  ): Promise<void> {
    try {
      const db = await this.getDB();
      const key = this.getKey(masjidId);
      await db.put(
        "events",
        {
          masjidId,
          events,
          lastFetch: Date.now(),
          eTag,
        },
        key,
      );
      console.log(`✅ Events saved for masjid ${masjidId}`);
    } catch (error) {
      console.error("Error saving events to IndexedDB:", error);
    }
  }

  async clearAll(): Promise<void> {
    try {
      const db = await this.getDB();
      await db.clear("events");
      console.log("All events cleared from IndexedDB");
    } catch (error) {
      console.error("Error clearing events from IndexedDB:", error);
    }
  }

  async clearMasjidEvents(masjidId: number): Promise<void> {
    try {
      const db = await this.getDB();
      const key = this.getKey(masjidId);
      await db.delete("events", key);
      console.log(`Events cleared for masjid ${masjidId}`);
    } catch (error) {
      console.error("Error clearing events from IndexedDB:", error);
    }
  }
}

export const eventsDB = new EventsDatabase();
export type { ParsedEvent };
